import { IconAngleRight, IconBuilding, IconUserCircle } from '@maia/icons';
import { CustomerDto } from '@maia/shared';
import { ActionButtonLink, TableCell } from '@maia/ui';
import { formatAddress, isConsumerOrOrganization } from '@maia/utils';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Customers } from './queryOptions';

type Customer = CustomerDto;

const columns: ColumnDef<Customer>[] = [
  {
    accessorKey: 'reference',
    header: 'Klantnummer',
    enableSorting: false,
    cell: ({ row }) => (
      <TableCell.Link
        to="/customers/$guid"
        params={{
          guid: row.original.guid,
        }}
      >
        {row.original.reference}
      </TableCell.Link>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Naam',
    cell: ({ row }) => (
      <TableCell.Activity
        icon={mapCustomerToIcon(row.original)}
        title={row.original.consumer.fullName}
      />
    ),
  },
  {
    accessorKey: 'address',
    header: 'Adres',
    enableSorting: false,
    cell: ({ row }) => (
      <TableCell.Address address={formatAddress(row.original.address)} />
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => <TableCell.Tag {...row.original.status} />,
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex gap-x-12 items-center">
        <ActionButtonLink
          icon={IconAngleRight}
          size="small"
          to="/customers/$guid"
          params={{
            guid: row.original.guid,
          }}
        />
      </div>
    ),
  },
];

function mapCustomerToIcon(customer: Customer) {
  return isConsumerOrOrganization(
    customer,
    () => <IconUserCircle className="size-24" />,
    () => <IconBuilding className="size-24" />,
  );
}

export function useCustomerTable(data?: Customers) {
  return useReactTable({
    columns,
    data: data?.response ?? [],
    rowCount: data?.response.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });
}
